import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart3,
  Mail,
  TicketPercent,
  Users,
  RefreshCw,
  Plus,
  Search,
  Power,
  Trash2,
  Send,
} from "lucide-react";

import { API, authHeaders } from "../context/AuthContext";
import { toast } from "sonner";
import AdminMarketingActions from "../components/AdminMarketingActions";
import AdminMarketingInsights from "../components/AdminMarketingInsights";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="dc-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#a8adbd]">
            {label}
          </div>
          <div className="mt-2 font-display text-3xl font-bold text-white">
            {value}
          </div>
          {hint && (
            <div className="mt-1 text-xs text-[#6f7484]">{hint}</div>
          )}
        </div>

        <div className="rounded-2xl bg-[#ff4458]/10 p-3 text-[#ff4458]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ active, redeemed, expired }) {
  if (redeemed) {
    return (
      <span className="rounded-full bg-[#32d583]/15 px-2.5 py-1 text-xs font-semibold text-[#32d583]">
        Wykorzystany
      </span>
    );
  }

  if (expired) {
    return (
      <span className="rounded-full bg-[#fdb022]/15 px-2.5 py-1 text-xs font-semibold text-[#fdb022]">
        Wygasły
      </span>
    );
  }

  return active ? (
    <span className="rounded-full bg-[#32d583]/15 px-2.5 py-1 text-xs font-semibold text-[#32d583]">
      Aktywny
    </span>
  ) : (
    <span className="rounded-full bg-[#fd267a]/15 px-2.5 py-1 text-xs font-semibold text-[#fd267a]">
      Wyłączony
    </span>
  );
}

export default function AdminMarketing() {
const [dashboard, setDashboard] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [sends, setSends] = useState([]);

  const [loading, setLoading] = useState(true);
  const [couponSearch, setCouponSearch] = useState("");
  const [subscriberSearch, setSubscriberSearch] = useState("");

  const [newCoupon, setNewCoupon] = useState({
    code: "",
    percent_off: 20,
    valid_days: 30,
    max_uses: 1,
    package_id: "",
    description: "",
  });

  const loadAll = async () => {
    setLoading(true);

    try {
      const headers = authHeaders();

      const [dashRes, couponRes, subRes, sendRes] = await Promise.all([
        axios.get(`${API}/admin/marketing/dashboard`, { headers }),
        axios.get(`${API}/admin/marketing/coupons?limit=200`, { headers }),
        axios.get(`${API}/admin/marketing/subscribers?limit=200`, {
          headers,
        }),
        axios.get(`${API}/admin/marketing/sends?limit=100`, { headers }),
      ]);

      setDashboard(dashRes.data);
      setCoupons(couponRes.data.items || []);
      setSubscribers(subRes.data.items || []);
      setSends(sendRes.data.items || []);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Nie udało się pobrać danych panelu"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const filteredCoupons = useMemo(() => {
    const q = couponSearch.trim().toLowerCase();

    if (!q) return coupons;

    return coupons.filter((c) =>
      [c.code, c.email, c.kind, c.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [coupons, couponSearch]);

  const filteredSubscribers = useMemo(() => {
    const q = subscriberSearch.trim().toLowerCase();

    if (!q) return subscribers;

    return subscribers.filter((s) =>
      String(s.email || "").toLowerCase().includes(q)
    );
  }, [subscribers, subscriberSearch]);

  const createCoupon = async () => {
    try {
      await axios.post(
        `${API}/admin/marketing/coupons`,
        {
          code: newCoupon.code.trim().toUpperCase(),
          percent_off: Number(newCoupon.percent_off),
          valid_days: Number(newCoupon.valid_days),
          max_uses: Number(newCoupon.max_uses),
          package_id: newCoupon.package_id || null,
          description: newCoupon.description || null,
        },
        { headers: authHeaders() }
      );

      toast.success("Kod rabatowy został utworzony");

      setNewCoupon({
        code: "",
        percent_off: 20,
        valid_days: 30,
        max_uses: 1,
        package_id: "",
        description: "",
      });

      await loadAll();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Nie udało się utworzyć kodu"
      );
    }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await axios.patch(
        `${API}/admin/marketing/coupons/${encodeURIComponent(
          coupon.code
        )}/status`,
        { active: coupon.active === false },
        { headers: authHeaders() }
      );

      toast.success(
        coupon.active === false ? "Kod włączony" : "Kod wyłączony"
      );

      await loadAll();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Nie udało się zmienić statusu"
      );
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Usunąć kod ${coupon.code}?`)) return;

    try {
      await axios.delete(
        `${API}/admin/marketing/coupons/${encodeURIComponent(coupon.code)}`,
        { headers: authHeaders() }
      );

      toast.success("Kod został usunięty");
      await loadAll();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Nie udało się usunąć kodu"
      );
    }
  };

  const isExpired = (coupon) => {
    if (!coupon.expires_at) return false;
    return new Date(coupon.expires_at).getTime() < Date.now();
  };
if (loading && !dashboard) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <RefreshCw className="h-7 w-7 animate-spin text-[#ff4458]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ff4458]/30 bg-[#ff4458]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#ff4458]">
            <BarChart3 className="h-3.5 w-3.5" />
            Panel administratora
          </div>

          <h1 className="mt-4 font-display text-4xl font-bold text-white">
            Marketing
          </h1>

          <p className="mt-2 text-sm text-[#a8adbd]">
            Newsletter, kupony, wysyłki i statystyki sprzedaży.
          </p>
        </div>

        <button
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#303545] bg-[#151821] px-4 py-2.5 text-sm font-semibold text-white hover:border-[#ff4458]/50 disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Odśwież
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          label="Użytkownicy"
          value={dashboard?.users_total || 0}
        />

        <StatCard
          icon={Mail}
          label="Newsletter"
          value={dashboard?.newsletter_active || 0}
          hint={`Wypisanych: ${dashboard?.newsletter_inactive || 0}`}
        />

        <StatCard
          icon={TicketPercent}
          label="Aktywne kupony"
          value={dashboard?.coupons_active || 0}
          hint={`Wykorzystanych: ${dashboard?.coupons_redeemed || 0}`}
        />

        <StatCard
          icon={BarChart3}
          label="Przychód"
          value={`${Number(
            dashboard?.revenue_total || 0
          ).toFixed(2)} PLN`}
          hint={`Płatności: ${dashboard?.payments_total || 0}`}
        />
      </div>

      <AdminMarketingActions onRefresh={loadAll} />

      <AdminMarketingInsights />

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="dc-card p-6 xl:col-span-1">
          <div className="mb-5 flex items-center gap-2">
            <Plus className="h-5 w-5 text-[#ff4458]" />
            <h2 className="font-display text-xl font-bold text-white">
              Nowy kod rabatowy
            </h2>
          </div>

          <div className="space-y-4">
            <input
              value={newCoupon.code}
              onChange={(e) =>
                setNewCoupon((v) => ({
                  ...v,
                  code: e.target.value.toUpperCase(),
                }))
              }
              placeholder="np. START20"
              className="w-full rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
            />

            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                min="1"
                max="90"
                value={newCoupon.percent_off}
                onChange={(e) =>
                  setNewCoupon((v) => ({
                    ...v,
                    percent_off: e.target.value,
                  }))
                }
                placeholder="Rabat %"
                className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
              />

              <input
                type="number"
                min="1"
                max="365"
                value={newCoupon.valid_days}
                onChange={(e) =>
                  setNewCoupon((v) => ({
                    ...v,
                    valid_days: e.target.value,
                  }))
                }
                placeholder="Dni ważności"
                className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
              />
            </div>

            <input
              type="number"
              min="1"
              value={newCoupon.max_uses}
              onChange={(e) =>
                setNewCoupon((v) => ({
                  ...v,
                  max_uses: e.target.value,
                }))
              }
              placeholder="Limit użyć"
              className="w-full rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
            />

            <select
              value={newCoupon.package_id}
              onChange={(e) =>
                setNewCoupon((v) => ({
                  ...v,
                  package_id: e.target.value,
                }))
              }
              className="w-full rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
            >
              <option value="">Wszystkie pakiety</option>
              <option value="single">1 kredyt</option>
              <option value="pack10">10 kredytów</option>
              <option value="monthly">15 kredytów miesięcznie</option>
            </select>

            <textarea
              value={newCoupon.description}
              onChange={(e) =>
                setNewCoupon((v) => ({
                  ...v,
                  description: e.target.value,
                }))
              }
              placeholder="Opis kodu"
              rows={3}
              className="w-full rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
            />

            <button
              onClick={createCoupon}
              disabled={!newCoupon.code.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-4 py-3 font-display text-sm font-semibold text-white disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Utwórz kod
            </button>
          </div>
        </div>

        <div className="dc-card p-6 xl:col-span-2">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-bold text-white">
              Kupony
            </h2>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6f7484]" />
              <input
                value={couponSearch}
                onChange={(e) => setCouponSearch(e.target.value)}
                placeholder="Szukaj kodu lub e-maila"
                className="rounded-full border border-[#2a2f3d] bg-[#0f1117] py-2.5 pl-9 pr-4 text-sm text-white outline-none focus:border-[#ff4458]"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#262a36] text-xs uppercase tracking-wider text-[#6f7484]">
                  <th className="px-3 py-3">Kod</th>
                  <th className="px-3 py-3">Rabat</th>
                  <th className="px-3 py-3">Typ</th>
                  <th className="px-3 py-3">Ważny do</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Akcje</th>
                </tr>
              </thead>

              <tbody>
                {filteredCoupons.map((coupon) => (
                  <tr
                    key={coupon.code}
                    className="border-b border-[#20242f] text-[#d7dae3]"
                  >
                    <td className="px-3 py-4">
                      <div className="font-mono font-bold text-white">
                        {coupon.code}
                      </div>
                      {coupon.email && (
                        <div className="mt-1 text-xs text-[#6f7484]">
                          {coupon.email}
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-4">
                      {coupon.percent_off}%
                    </td>

                    <td className="px-3 py-4">
                      {coupon.kind || "manual"}
                    </td>

                    <td className="px-3 py-4">
                      {coupon.expires_at
                        ? new Date(coupon.expires_at).toLocaleString()
                        : "—"}
                    </td>

                    <td className="px-3 py-4">
                      <StatusBadge
                        active={coupon.active !== false}
                        redeemed={coupon.redeemed === true}
                        expired={isExpired(coupon)}
                      />
                    </td>

                    <td className="px-3 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => toggleCoupon(coupon)}
                          className="rounded-lg border border-[#303545] p-2 text-[#a8adbd] hover:border-[#ff4458]/40 hover:text-white"
                          title={
                            coupon.active === false ? "Włącz" : "Wyłącz"
                          }
                        >
                          <Power className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => deleteCoupon(coupon)}
                          className="rounded-lg border border-[#303545] p-2 text-[#fd267a] hover:border-[#fd267a]/50"
                          title="Usuń"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredCoupons.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-10 text-center text-[#6f7484]"
                    >
                      Brak kuponów.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="dc-card p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-bold text-white">
              Subskrybenci newslettera
            </h2>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6f7484]" />
              <input
                value={subscriberSearch}
                onChange={(e) => setSubscriberSearch(e.target.value)}
                placeholder="Szukaj e-maila"
                className="rounded-full border border-[#2a2f3d] bg-[#0f1117] py-2.5 pl-9 pr-4 text-sm text-white outline-none focus:border-[#ff4458]"
              />
            </div>
          </div>

          <div className="max-h-[500px] overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#262a36] text-xs uppercase tracking-wider text-[#6f7484]">
                  <th className="px-3 py-3">E-mail</th>
                  <th className="px-3 py-3">Język</th>
                  <th className="px-3 py-3">Kurs</th>
                  <th className="px-3 py-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredSubscribers.map((subscriber) => (
                  <tr
                    key={subscriber.email}
                    className="border-b border-[#20242f] text-[#d7dae3]"
                  >
                    <td className="px-3 py-4 text-white">
                      {subscriber.email}
                    </td>

                    <td className="px-3 py-4">
                      {subscriber.newsletter_lang || "pl"}
                    </td>

                    <td className="px-3 py-4">
                      Dzień {subscriber.drip_day || 0}
                    </td>

                    <td className="px-3 py-4">
                      {subscriber.newsletter_subscribed ? (
                        <span className="text-[#32d583]">Zapisany</span>
                      ) : (
                        <span className="text-[#fd267a]">Wypisany</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dc-card p-6">
          <div className="mb-5 flex items-center gap-2">
            <Send className="h-5 w-5 text-[#ff4458]" />
            <h2 className="font-display text-xl font-bold text-white">
              Historia wysyłek
            </h2>
          </div>

          <div className="max-h-[500px] overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#262a36] text-xs uppercase tracking-wider text-[#6f7484]">
                  <th className="px-3 py-3">Odbiorca</th>
                  <th className="px-3 py-3">Temat</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Data</th>
                </tr>
              </thead>

              <tbody>
                {sends.map((send) => (
                  <tr
                    key={send.id || `${send.to}-${send.created_at}`}
                    className="border-b border-[#20242f] text-[#d7dae3]"
                  >
                    <td className="px-3 py-4 text-white">
                      {send.to || send.email || "—"}
                    </td>

                    <td className="px-3 py-4">
                      {send.subject || "—"}
                    </td>

                    <td className="px-3 py-4">
                      {send.status || "—"}
                    </td>

                    <td className="px-3 py-4">
                      {send.created_at || send.sent_at
                        ? new Date(
                            send.created_at || send.sent_at
                          ).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}

                {sends.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-10 text-center text-[#6f7484]"
                    >
                      Brak zapisanych wysyłek.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
